import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Circle, ExternalLink } from "lucide-react";
import { getLaunchStatus, getTokenInfo, saveTokenSettings } from "@/api";
import { PoolWizard } from "@/components/operator/PoolWizard";

const Step = ({ done, title, children, tid }) => (
  <div className="flex gap-3 py-3 border-b border-white/5" data-testid={tid} data-done={done ? "true" : "false"}>
    {done ? <CheckCircle2 className="text-emerald-400 shrink-0" size={22} /> : <Circle className="text-slate-500 shrink-0" size={22} />}
    <div className="flex-1"><div className="font-display font-bold">{title}</div><div className="text-sm text-slate-400 mt-1">{children}</div></div>
  </div>
);

export const PoolSetup = ({ token, showToast }) => {
  const [st, setSt] = useState(null);
  const load = useCallback(() => getLaunchStatus(token).then(setSt).catch(() => {}), [token]);
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, [load]);
  return (
    <div data-testid="pool-setup">
      <PoolWizard token={token} showToast={showToast} onChange={load} />
      {st && (
        <div className="mt-6 card-tile" data-testid="launch-checklist">
          <div className="font-display text-lg font-bold">Launch checklist</div>
          <Step tid="check-pool-created" done={st.pool_created} title="1. Pool created">FLAME/CRO 1% pool on VVS V3{st.pool.address ? `: ${st.pool.address}` : "."}</Step>
          <Step tid="check-position-added" done={st.position_added} title="2. Position added">The Safe holds {st.safe_positions} VVS V3 position NFT(s).</Step>
          <Step tid="check-position-locked" done={st.position_locked} title="3. Position locked for 12 months">
            {st.lock?.deployed
              ? <>Held by <a className="text-cyan-400 underline" href={st.lock.url} target="_blank" rel="noreferrer">FlameLPLock <ExternalLink size={11} className="inline" /></a> until {new Date(st.lock.unlock_time * 1000).toISOString().slice(0, 10)}.</>
              : "Done in wizard step 4 once the FlameLPLock contract is deployed."}
          </Step>
          <Step tid="check-token-page" done={st.token_page_updated} title="4. Token page and white paper updated">Add social links in Token Settings, then publish a white paper update (currently v{st.whitepaper_version}).</Step>
        </div>
      )}
    </div>
  );
};

export const TokenSettings = ({ token, showToast }) => {
  const [f, setF] = useState(null);
  useEffect(() => { getTokenInfo().then((t) => setF({ socials: { website: "", x: "", telegram: "", discord: "", ...t.socials }, lock_url: t.lock_url || "", position_id: "" })); }, []);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, socials: { ...x.socials, [k]: v } }));
  const save = () => saveTokenSettings(token, f).then(() => showToast("Token settings saved")).catch((e) => showToast(e?.response?.data?.detail || "Failed", "error"));
  const inp = "w-full bg-[#12131C] border border-[#2A2E45] rounded-lg px-3 py-2 text-sm";
  return (
    <div className="card-tile space-y-3 max-w-xl" data-testid="token-settings">
      {["website", "x", "telegram", "discord"].map((k) => (
        <label key={k} className="block text-sm capitalize text-slate-300">{k}<input data-testid={`settings-${k}`} className={inp} value={f.socials[k]} onChange={(e) => set(k, e.target.value)} placeholder="https://…" /></label>
      ))}
      <label className="block text-sm text-slate-300">Liquidity lock link (Team Finance)<input data-testid="settings-lock-url" className={inp} value={f.lock_url} onChange={(e) => setF({ ...f, lock_url: e.target.value })} /></label>
      <label className="block text-sm text-slate-300">VVS V3 position ID<input data-testid="settings-position-id" className={inp} value={f.position_id} onChange={(e) => setF({ ...f, position_id: e.target.value })} /></label>
      <button data-testid="settings-save" onClick={save} className="btn-tactile btn-flame text-sm">Save</button>
    </div>
  );
};
